/**
 * Public animation states; artwork poses are mapped independently in mascot.poses.ts.
 *
 * `duration` is the whole performance, not just the expressive core. Every expression is built
 * as anticipation -> core -> settle: the core stays quick (a wink is still ~0.2s of eyelid), but
 * the character leans in beforehand and relaxes afterwards instead of snapping between poses.
 * Chaining bare cores is what made the sequence read as state changes rather than acting.
 */
export const MASCOT_STATES = [
  { id: 'idle', name: 'Idle', detail: 'Blink & ear twitch', duration: 3, context: 'Everyday companion' },
  { id: 'wink', name: 'Wink', detail: 'A little hello', duration: 2.8, context: 'Greetings' },
  { id: 'happy-jump', name: 'Happy jump', detail: 'A moment of joy', duration: 2.9, context: 'Successful actions' },
  { id: 'show-love', name: 'Show some love', detail: 'A heartfelt hug', duration: 3, context: 'Positive confirmations' },
  { id: 'curious', name: 'Curious', detail: 'A thoughtful tilt', duration: 3.4, context: 'Empty states & guidance' },
  { id: 'pounce', name: 'Playful pounce', detail: 'Ready, set, hop', duration: 1.5, context: 'Occasional interactions' },
  { id: 'thank-you', name: 'Thank you', detail: 'A grateful little bow', duration: 1.6, context: 'Bookings & milestones' },
  { id: 'sleep', name: 'Nap time', detail: 'Slow, sleepy breaths', duration: 4.5, context: 'Long passive waits' },
  { id: 'peek', name: 'Peek', detail: 'Just checking in', duration: 2.2, context: 'Onboarding & discovery' },
  { id: 'follow', name: 'Follow', detail: 'A curious companion', duration: 4, context: 'Pointer interaction' },
] as const;

export type BunnyAnimation = typeof MASCOT_STATES[number]['id'];
export type LoaderPhase = 'starting' | 'gathering' | 'working' | 'almost-there' | 'done';
export type MascotState = BunnyAnimation | LoaderPhase;
export const LOADER_PHASES: { id: LoaderPhase; label: string }[] = [
  { id: 'starting', label: 'Starting...' },
  { id: 'gathering', label: 'Gathering magic...' },
  { id: 'working', label: 'Working on it...' },
  { id: 'almost-there', label: 'Almost there...' },
  { id: 'done', label: 'Done!' },
];

export function loaderPhaseAt(elapsedMs: number): Exclude<LoaderPhase, 'done'> | null {
  if (elapsedMs < 450) return null;
  if (elapsedMs < 1300) return 'starting';
  if (elapsedMs < 2600) return 'gathering';
  if (elapsedMs < 5000) return 'working';
  return 'almost-there';
}
